import { useEffect, useRef, useState } from 'react'
import {
  clearFunnel,
  isApiError,
  isEmailAlreadyRegistered,
  readFunnel,
  useApplyPromo,
  useChangeCheckoutPlan,
  useCheckoutSession,
  useEnterCheckout,
  usePayCheckout,
  usePlanCatalogue,
  useStartCheckout,
  writeFunnel,
  type ChildInput,
  type SignupPath,
  type StartCheckout,
} from '@lb/domain'

import { LOCALE_PREFIX, splitLocalePath, useTranslation } from '@lb/i18n'

import { auth, publicHttp } from '../../lib/client'

import { useContentLocale } from '../../lib/content-locale'
import { ConfirmSkeleton, ConfirmStep } from './confirm'
import { LandingScreen } from './landing'
import {
  DeclinedScreen,
  EmailExistsScreen,
  InvoicedScreen,
  PendingScreen,
  WelcomeScreen,
} from './outcomes'
import { resolveIntent, type SignupIntent } from './signup-intent'
import { AccountStep, CrewStep, OrganisationStep, PractitionerStep, type AccountValues } from './steps'

/**
 * The funnel, as one linear controller.
 *
 * Nine steps and four terminal outcomes. Deliberately NOT a state-machine
 * library: `CheckoutStatus` on the server is already the machine, and a
 * client-side chart would be a second one that disagrees. What lives here is
 * only the part the server does not own — which step of the form we are on.
 *
 * Once a session exists, **the server's status decides the screen**, not our
 * step counter. That is what makes the pending → active transition work without
 * us modelling it.
 */

type Step = 'landing' | 'account' | 'extras' | 'confirm' | 'emailExists'

export function OnboardingFunnel({
  onEnterApp,
  onSignIn,
  intent = {},
  onIntentApplied,
}: {
  onEnterApp: () => void
  onSignIn: () => void
  /** A plan chosen on the public website (`/signup?audience=…&plan=…`). */
  intent?: SignupIntent
  /** Called once the intent is applied, so the link's query can be dropped. */
  onIntentApplied?: () => void
}) {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const [step, setStep] = useState<Step>('landing')
  const [path, setPath] = useState<SignupPath>('PARENT')
  const [planKey, setPlanKey] = useState<string>()
  const [account, setAccount] = useState<AccountValues>()
  const [children, setChildren] = useState<ChildInput[]>([])
  const [orgSeats, setOrgSeats] = useState<number>(25)
  const [checkoutId, setCheckoutId] = useState<string>()
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>()

  // Resume a funnel left mid-flow. The password is never among what returns.
  useEffect(() => {
    const saved = readFunnel()
    if (saved.checkoutId) {
      setCheckoutId(saved.checkoutId)
      setStep('confirm')
    }
    if (saved.path) setPath(saved.path)
    if (saved.planKey) setPlanKey(saved.planKey)
    if (saved.organisation?.seatCount) setOrgSeats(saved.organisation.seatCount)
    if (saved.children.length) setChildren(saved.children)
    if (saved.fullName || saved.email) {
      setAccount({
        fullName: saved.fullName ?? '',
        email: saved.email ?? '',
        password: '',
        relationship: saved.relationship as AccountValues['relationship'],
        marketingConsent: saved.marketingConsent,
      })
    }
  }, [])

  /*
   * A plan chosen on the website wins over a funnel left mid-flow: the visitor
   * just said what they want. The details they typed before are kept, a
   * checkout started for another plan is let go (it stays a harmless DRAFT),
   * and the link's query is dropped once applied, so a refresh later in the
   * flow does not start it over.
   */
  const [landingAudience, setLandingAudience] = useState<SignupPath | undefined>(intent.audience)
  const intentCatalogue = usePlanCatalogue(publicHttp, intent.audience ?? 'PARENT', locale)
  const intentApplied = useRef(false)
  useEffect(() => {
    if (intentApplied.current || (!intent.plan && !intent.audience)) return
    if (intent.plan && intentCatalogue.isPending) return
    intentApplied.current = true
    const resolved = resolveIntent(intent, intentCatalogue.data?.items ?? [])
    if (resolved?.step === 'account') {
      const seats = resolved.path === 'ORG' ? (intent.seats ?? readFunnel().organisation?.seatCount ?? 25) : undefined
      setPath(resolved.path)
      setPlanKey(resolved.planKey)
      setCheckoutId(undefined)
      if (seats) setOrgSeats(seats)
      const saved = readFunnel()
      writeFunnel({
        ...saved,
        path: resolved.path,
        planKey: resolved.planKey,
        checkoutId: undefined,
        ...(seats ? { organisation: { ...saved.organisation, seatCount: seats } } : {}),
      })
      setStep('account')
    } else if (resolved) {
      setLandingAudience(resolved.path)
    }
    onIntentApplied?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applied once, when the catalogue answers
  }, [intentCatalogue.isPending])

  const session = useCheckoutSession(publicHttp, checkoutId)
  const start = useStartCheckout(publicHttp)
  const promo = useApplyPromo(publicHttp, checkoutId)
  const pay = usePayCheckout(publicHttp, checkoutId)
  const enter = useEnterCheckout(publicHttp, checkoutId)
  const changePlan = useChangeCheckoutPlan(publicHttp, checkoutId)

  const persist = (patch: Partial<ReturnType<typeof readFunnel>>) => {
    writeFunnel({ ...readFunnel(), ...patch })
  }

  async function submit(extras: Partial<StartCheckout>) {
    if (!account || !planKey) return
    setFieldErrors(undefined)

    try {
      const created = await start.mutateAsync({
        path,
        planKey,
        account: {
          fullName: account.fullName,
          email: account.email,
          password: account.password,
          ...(account.relationship ? { relationship: account.relationship } : {}),
          ...(account.marketingConsent ? { marketingConsent: true } : {}),
          /*
           * The language they are ACTUALLY signing up in, not a constant.
           *
           * This was hardcoded `'fr'`, and it is the only thing that ever
           * writes `users.locale` — there is no route to change it afterwards.
           * So somebody who completed the funnel in English had their account
           * stamped French permanently, and the backend resolves transactional
           * mail from this field: the confirmation and password-reset messages
           * would arrive in a language they had explicitly not chosen, with no
           * setting anywhere to correct it.
           *
           * `useContentLocale()` is the same one-control derivation the rest of
           * the product uses (§6.3), so this follows the language button.
           */
          locale,
        },
        children: path === 'PARENT' ? children : [],
        ...extras,
      })

      setCheckoutId(created.id)
      persist({ checkoutId: created.id })
      setStep('confirm')
    } catch (error) {
      // Never a dead end — the backend raises this code so we can route.
      if (isEmailAlreadyRegistered(error)) {
        setStep('emailExists')
        return
      }
      // The server's Zod flatten lands on the same fields the form named.
      if (isApiError(error)) {
        const fields = error.fieldErrors
        if (fields) {
          setFieldErrors(fields)
          setStep('account')
          return
        }
      }
      throw error
    }
  }

  // ── The server's status wins once a session exists ────────────────────────
  const status = session.data?.status

  if (session.data && status === 'ACTIVE') {
    return (
      <WelcomeScreen
        busy={enter.isPending}
        onEnter={() => {
          /*
           * Trade the checkout id for a real auth session before we navigate.
           *
           * Without this, clicking Enter loaded `/` and IndexScreen re-rendered
           * the funnel: the account was ACTIVE on the backend but the browser
           * still had no tokens, since the password was never persisted across
           * the Stripe redirect. The backend's new `POST /checkout/{id}/enter`
           * mints tokens for the linked user — same shape as sign-in, with the
           * checkout id as the capability (already held by this funnel).
           *
           * `clearFunnel()` runs AFTER the mint succeeds — a failure at this
           * step should leave the funnel state intact so the visitor can
           * retry, not drop them into a fresh signup with no way back.
           *
           * `onEnter` is typed as `() => void` because the WelcomeScreen prop
           * is (a callback fired by the click, no return expected). The async
           * work is wrapped in an IIFE and voided so the handler's return
           * stays void — eslint's `no-misused-promises` refuses a promise
           * where a void callback is expected.
           */
          void (async () => {
            try {
              const pair = await enter.mutateAsync()
              auth.set(pair)
              clearFunnel()
              onEnterApp()
            } catch {
              // On failure we swallow — the button stays visible for another
              // click. A network hiccup should not restart the funnel.
            }
          })()
        }}
      />
    )
  }

  if (session.data && status === 'PENDING_ACTIVATION') {
    return <PendingScreen session={session.data} />
  }

  if (session.data && status === 'INVOICED') {
    return <InvoicedScreen session={session.data} />
  }

  if (session.data && status === 'DECLINED') {
    return (
      <DeclinedScreen
        session={session.data}
        busy={pay.isPending}
        onRetry={() => void redirectToStripe()}
      />
    )
  }

  // ── Otherwise the form decides ────────────────────────────────────────────

  async function redirectToStripe() {
    const origin = window.location.origin
    /*
     * Preserve the visitor's language on the return trip. Stripe's Checkout
     * loses the origin locale entirely — the visitor is on checkout.stripe.com,
     * not on our domain — so if the successUrl carries no `/en/` prefix, an
     * English signup lands back on the French default (fr is the source
     * locale, served at the root). Reported by a member who paid in English
     * and returned to a French Welcome screen.
     */
    // `useContentLocale()` returns a `string`; read the narrowed union off
    // the URL instead of asserting it, since the URL is our source of truth
    // for which locale prefix Vite is serving.
    const { locale: uiLocale } = splitLocalePath(window.location.pathname)
    const prefix = LOCALE_PREFIX[uiLocale]
    const updated = await pay.mutateAsync({
      successUrl: `${origin}${prefix}/signup?paid=1`,
      cancelUrl: `${origin}${prefix}/signup?cancelled=1`,
    })
    // The org path never gets a paymentUrl — it ends in an invoice.
    if (updated.paymentUrl) window.location.assign(updated.paymentUrl)
  }

  if (step === 'emailExists') {
    return (
      <EmailExistsScreen
        email={account?.email ?? ''}
        onSignIn={onSignIn}
        onBack={() => setStep('account')}
      />
    )
  }

  if (step === 'confirm') {
    if (!session.data) return <ConfirmSkeleton />
    return (
      <ConfirmStepContainer
        session={session.data}
        pathAudience={session.data.path}
        locale={locale}
        payBusy={pay.isPending}
        switching={changePlan.isPending}
        promoBusy={promo.isPending}
        /* Through `t`. It was a French literal, so an English signup was told
           its promo code had failed in a language it had not been typed in. */
        promoError={promo.isError ? t('onboarding.promoRejected') : undefined}
        onApplyPromo={(code) => promo.mutate(code)}
        onBack={() => setStep('extras')}
        onPay={() => void redirectToStripe()}
        onSwitchPlan={(newKey) => {
          /*
           * Server-side re-quote on the existing session, NOT a second
           * `start` call.
           *
           * The previous version called `submit({ planKey })`, which POSTs
           * `/checkout` with the same email — refused with
           * EMAIL_ALREADY_REGISTERED because the account created on the
           * first start is still there — and the funnel routed to
           * EmailExistsScreen. From the visitor's side, tapping "Monthly"
           * on the confirm screen bounced them to "You already have an
           * account" with no explanation.
           */
          if (!account || newKey === session.data.planKey) return
          setPlanKey(newKey)
          persist({ planKey: newKey })
          changePlan.mutate(newKey)
        }}
      />
    )
  }

  if (step === 'extras') {
    if (path === 'PARENT') {
      return (
        <CrewStep
          initial={children}
          busy={start.isPending}
          onBack={() => setStep('account')}
          onSubmit={(payload) => {
            /*
             * `submit` reads `children` off the closure, and `setChildren`
             * doesn't flush before the next line — so passing children
             * through state alone meant every new signup posted `children:
             * []` and dropped names the parent had just typed. Reported by
             * a member (Aslam) whose crew survived onboarding but never
             * reached the database.
             *
             * Pass children (and coParent) THROUGH `extras`. `submit`
             * spreads extras after its own defaults, so this wins.
             */
            setChildren(payload.children)
            persist({ children: payload.children })
            void submit({
              children: payload.children,
              ...(payload.coParent ? { coParentName: payload.coParent } : {}),
            })
          }}
        />
      )
    }

    if (path === 'ORG') {
      return (
        <OrganisationStep
          busy={start.isPending}
          seats={orgSeats}
          contactName={account?.fullName ?? ''}
          onBack={() => setStep('account')}
          onSubmit={(organisation) =>
            void submit({ organisation: { ...organisation, seatCount: orgSeats } })
          }
        />
      )
    }

    return (
      <PractitionerStep
        path={path}
        busy={start.isPending}
        onBack={() => setStep('account')}
        onSubmit={(practitioner) => void submit({ practitioner })}
      />
    )
  }

  if (step === 'account') {
    return (
      <AccountStep
        path={path}
        initial={account ?? {}}
        serverErrors={fieldErrors}
        busy={start.isPending}
        onBack={() => setStep('landing')}
        onSubmit={(values) => {
          setAccount(values)
          // Everything BUT the password.
          persist({
            fullName: values.fullName,
            email: values.email,
            relationship: values.relationship,
            marketingConsent: values.marketingConsent,
          })
          setStep('extras')
        }}
      />
    )
  }

  return (
    <LandingScreen
      key={landingAudience ?? 'default'}
      initialAudience={landingAudience}
      onChoose={(chosenPath, chosenPlan, seats) => {
        setPath(chosenPath)
        setPlanKey(chosenPlan)
        if (seats) setOrgSeats(seats)
        persist({
          path: chosenPath,
          planKey: chosenPlan,
          ...(seats ? { organisation: { seatCount: seats } } : {}),
        })
        setStep('account')
      }}
    />
  )
}

/**
 * ConfirmStep needs the audience's plan catalogue to render the switcher on
 * the left of the screen. Kept as a container so the fetch (`usePlanCatalogue`)
 * only runs when the confirm step is actually mounted, and so the switcher
 * can render a plain skeleton while the plans are loading.
 */
function ConfirmStepContainer({
  session,
  pathAudience,
  locale,
  payBusy,
  switching,
  promoBusy,
  promoError,
  onSwitchPlan,
  onApplyPromo,
  onBack,
  onPay,
}: {
  session: NonNullable<ReturnType<typeof useCheckoutSession>['data']>
  pathAudience: SignupPath
  locale: string
  payBusy: boolean
  switching: boolean
  promoBusy: boolean
  promoError?: string
  onSwitchPlan: (planKey: string) => void
  onApplyPromo: (code: string | null) => void
  onBack: () => void
  onPay: () => void
}) {
  const plans = usePlanCatalogue(publicHttp, pathAudience, locale)
  if (!plans.data) return <ConfirmSkeleton />

  return (
    <ConfirmStep
      session={session}
      plans={plans.data.items}
      busy={payBusy}
      switching={switching}
      promoBusy={promoBusy}
      promoError={promoError}
      onSwitchPlan={onSwitchPlan}
      onApplyPromo={onApplyPromo}
      onBack={onBack}
      onPay={onPay}
    />
  )
}
